import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ChevronRight, LogOut, Menu, X, MessageSquare } from 'lucide-react';
import { connectSocket } from '../../services/chatSocket';
import ThemeToggle from './ThemeToggle';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Once the user scrolls past the top, keep navbar solid permanently.
  // This prevents the background from flashing back to transparent when
  // clicking a link resets the scroll position (causing the "shake").
  const [navSolid, setNavSolid] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
      if (isScrolled) setNavSolid(true);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const token = sessionStorage.getItem('accessToken');
    if (!token) return;
    
    const socket = connectSocket(token);
    
    socket.emit('chat:get_conversations', (response) => {
      if (response.success && response.conversations) {
        const count = response.conversations.reduce((sum, c) => sum + Number(c.unread_count || 0), 0);
        setUnreadCount(count);
      }
    });

    const handleNewMessage = (message) => {
      if (message.sender_role === 'user') {
        socket.emit('chat:get_conversations', (response) => {
          if (response.success && response.conversations) {
            const count = response.conversations.reduce((sum, c) => sum + Number(c.unread_count || 0), 0);
            setUnreadCount(count);
          }
        });
      }
    };
    
    socket.on('chat:new_message', handleNewMessage);
    
    return () => {
      socket.off('chat:new_message', handleNewMessage);
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (err) {
      toast.error('Logout failed');
    }
  };

  const links = [
    { label: 'Features', href: '/#features' },
    { label: 'How it works', href: '/#how-it-works' },
    { label: 'Pricing', href: '/pricing' },
  ];

  const isDashboardOrAdmin = 
    (location.pathname || '').startsWith('/dashboard') || 
    (location.pathname || '').startsWith('/admin') ||
    (location.pathname || '').startsWith('/agent') ||
    (location.pathname || '').startsWith('/notifications');

  if (isDashboardOrAdmin) return null;

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: 72,
        // Use navSolid (sticky once scrolled) OR mobileMenuOpen to keep bg visible.
        // This eliminates the shake caused by scroll-reset on link navigation.
        background: (navSolid || scrolled || mobileMenuOpen) ? 'var(--bg-navbar)' : 'transparent',
        backdropFilter: (navSolid || scrolled || mobileMenuOpen) ? 'blur(12px)' : 'none',
        borderBottom: (navSolid || scrolled || mobileMenuOpen) ? '1px solid var(--border)' : '1px solid transparent',
        transition: 'background 0.15s ease, border-color 0.15s ease',
        willChange: 'transform',
      }}>
        <div className="container" style={{ 
          height: '100%',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          {/* Logo */}
          <Link to="/" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8,
            textDecoration: 'none'
          }}>
            <img 
              src="/SB.png" 
              alt="SignalBros Logo" 
              style={{ 
                width: 28, 
                height: 28, 
                borderRadius: 6 
              }} 
            />
            <span style={{ 
              fontSize: 18, 
              fontWeight: 600,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em'
            }}>SignalBros</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="desktop-only" style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 32
          }}>
            {links.map(l => (
              <a 
                key={l.label} 
                href={l.href}
                style={{ 
                  fontSize: 14, 
                  color: 'var(--text-secondary)',
                  fontWeight: 450,
                  transition: 'var(--transition)'
                }}
                onMouseEnter={e => e.target.style.color = 'var(--text-primary)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}
              >
                {l.label}
              </a>
            ))}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="desktop-only" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ThemeToggle />
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link 
                    to="/admin/chat"
                    style={{
                      color: 'var(--text-primary)',
                      fontSize: 14,
                      fontWeight: 500,
                      textDecoration: 'none',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                      marginRight: 8,
                      transition: 'var(--transition)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary-accent)';
                      e.currentTarget.style.color = 'var(--primary-accent)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }}
                  >
                    <MessageSquare size={16} /> Support
                    {unreadCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        backgroundColor: '#ff4932',
                        color: 'white',
                        fontSize: 10,
                        padding: '2px 6px',
                        borderRadius: 10,
                        fontWeight: 'bold'
                      }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                <Link 
                  to={user.role === 'admin' ? '/admin' : '/dashboard'}
                  style={{ 
                    background: 'var(--primary-accent)', 
                    color: 'white', 
                    padding: '8px 16px', 
                    borderRadius: 'var(--radius-md)',
                    fontSize: 14, 
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'var(--transition)'
                  }}
                >
                  {user.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                </Link>
                <button 
                  onClick={handleLogout}
                  style={{ 
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    padding: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'var(--transition)'
                  }}
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login"
                  style={{ 
                    color: 'var(--text-secondary)',
                    fontSize: 14, 
                    fontWeight: 450,
                    textDecoration: 'none',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={e => e.target.style.color = 'var(--text-primary)'}
                  onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}
                >
                  Sign in
                </Link>
                <Link 
                  to="/register"
                  style={{ 
                    background: 'var(--primary-accent)', 
                    color: 'white', 
                    padding: '8px 16px', 
                    borderRadius: 'var(--radius-md)',
                    fontSize: 14, 
                    fontWeight: 500,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'var(--transition)'
                  }}
                >
                  Get started <ChevronRight size={16} />
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-only"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              padding: 4
            }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div style={{
            position: 'absolute',
            top: 72,
            left: 0,
            right: 0,
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border)',
            padding: '24px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            zIndex: 999
          }}>
            {links.map(l => (
              <a 
                key={l.label} 
                href={l.href}
                onClick={() => setMobileMenuOpen(false)}
                style={{ 
                  fontSize: 16, 
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                  textDecoration: 'none'
                }}
              >
                {l.label}
              </a>
            ))}
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            {user ? (
              <>
                <Link 
                  to={user.role === 'admin' ? '/admin' : '/dashboard'}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ 
                    fontSize: 16, 
                    color: 'var(--primary-accent)',
                    fontWeight: 600,
                    textDecoration: 'none'
                  }}
                >
                  {user.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                </Link>
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  style={{ 
                    background: 'none',
                    border: 'none',
                    color: 'var(--danger)',
                    fontSize: 16,
                    fontWeight: 500,
                    textAlign: 'left',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ 
                    fontSize: 16, 
                    color: 'var(--text-primary)',
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                >
                  Sign in
                </Link>
                <Link 
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ 
                    background: 'var(--primary-accent)', 
                    color: 'white', 
                    padding: '12px', 
                    borderRadius: 'var(--radius-md)',
                    fontSize: 15, 
                    fontWeight: 600,
                    textDecoration: 'none',
                    textAlign: 'center'
                  }}
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        )}
      </nav>
    </>
  );
}
